/* global trapFocus, removeTrapFocus, theme */

if (!customElements.get('hotspots-image')) {
  class HotspotsImage extends HTMLElement {
    constructor() {
      super();

      this.handleClick = this.handleClick.bind(this);
      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
      document.removeEventListener('click', this.clickOutsideHandler);
      document.removeEventListener('keyup', this.keyUpHandler);
      this.closeMobileSheet();
    }

    /**
     * Returns true when the theme is using its desktop breakpoint.
     * @returns {boolean}
     */
    isDesktop() {
      if (
        window.theme &&
        theme.mediaMatches &&
        typeof theme.mediaMatches.md === 'boolean'
      ) {
        return theme.mediaMatches.md;
      }

      return window.matchMedia('(min-width: 769px)').matches;
    }

    /**
     * Handles clicks on hotspot buttons.
     * @param {MouseEvent} evt
     */
    handleClick(evt) {
      const button = evt.target.closest('.hotspot__btn');

      if (!button || !this.contains(button)) return;

      const hotspot = button.closest('.hotspot');
      if (!hotspot) return;

      if (hotspot === this.activeHotspot) {
        this.closeActiveHotspot(evt);
        return;
      }

      if (this.activeHotspot) {
        this.switchActiveHotspot(hotspot);
      } else {
        this.activateHotspot(hotspot);
      }
    }

    /**
     * Changes directly from one open hotspot to another without closing
     * and reopening the mobile backdrop.
     * @param {Element} hotspot
     */
    switchActiveHotspot(hotspot) {
      const previousHotspot = this.activeHotspot;
      const previousCard = this.activeCard;

      if (previousHotspot) {
        this.setHotspotActiveState(previousHotspot, false);
      }

      if (previousCard) {
        previousCard.style = null;
      }

      removeTrapFocus();
      this.activeCard = null;
      this.activateHotspot(hotspot);
    }

    /**
     * Handles clicks outside the active hotspot card.
     * @param {MouseEvent} evt
     */
    handleClickOutside(evt) {
      if (evt.target.closest('.hotspot__btn, .hotspot__card')) return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    /**
     * Handles Escape-key presses.
     * @param {KeyboardEvent} evt
     */
    handleKeyup(evt) {
      if (evt.key !== 'Escape') return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    /**
     * Cleans up after the closing opacity transition.
     * @param {TransitionEvent} evt
     */
    handleTransitionEnd(evt) {
      if (
        this.activeHotspot ||
        !this.activeCard ||
        evt.target !== this.activeCard ||
        evt.propertyName !== 'opacity'
      ) {
        return;
      }

      this.classList.add('overflow-hidden');
      this.activeCard.style = null;
      this.activeCard = null;

      this.removeEventListener('transitionend', this.transitionHandler);
    }

    /**
     * Opens a hotspot card.
     * @param {Element} hotspot
     */
    activateHotspot(hotspot) {
      this.activeCard = hotspot.querySelector('.hotspot__card');
      if (!this.activeCard) return;

      this.checkCardPosition();
      this.classList.remove('overflow-hidden');
      this.setHotspotActiveState(hotspot, true);

      if (!this.isDesktop()) {
        this.openMobileSheet();
      }

      trapFocus(hotspot);

      this.clickOutsideHandler =
        this.clickOutsideHandler || this.handleClickOutside.bind(this);

      this.keyUpHandler =
        this.keyUpHandler || this.handleKeyup.bind(this);

      this.resizeHandler =
        this.resizeHandler || this.closeActiveHotspot.bind(this);

      document.addEventListener('click', this.clickOutsideHandler);
      document.addEventListener('keyup', this.keyUpHandler);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Closes the active hotspot card.
     * @param {Event} evt
     */
    closeActiveHotspot(evt = {}) {
      const isResize = evt.type === 'on:debounced-resize';

      if (!isResize) {
        this.transitionHandler =
          this.transitionHandler || this.handleTransitionEnd.bind(this);

        this.addEventListener('transitionend', this.transitionHandler);
      }

      if (this.activeHotspot) {
        this.setHotspotActiveState(this.activeHotspot, false);
        removeTrapFocus();
        this.closeMobileSheet();

        if (isResize) {
          this.classList.add('overflow-hidden');

          if (this.activeCard) {
            this.activeCard.style = null;
          }

          this.activeCard = null;
        }
      }

      document.removeEventListener('click', this.clickOutsideHandler);
      document.removeEventListener('keyup', this.keyUpHandler);
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Adds the mobile bottom-sheet state and prevents background scrolling.
     */
    openMobileSheet() {
      this.classList.add('has-active-hotspot');

      const section = this.closest('.cc-product-features');
      if (section) section.classList.add('hotspot-sheet-is-open');

      document.documentElement.classList.add('hotspot-sheet-open');

      if (this.activeCard) {
        this.activeCard.setAttribute('role', 'dialog');
        this.activeCard.setAttribute('aria-modal', 'true');
      }
    }

    /**
     * Removes the mobile bottom-sheet state.
     */
    closeMobileSheet() {
      this.classList.remove('has-active-hotspot');

      const section = this.closest('.cc-product-features');
      if (section) section.classList.remove('hotspot-sheet-is-open');

      if (this.activeCard) {
        this.activeCard.removeAttribute('role');
        this.activeCard.removeAttribute('aria-modal');
      }

      requestAnimationFrame(() => {
        if (!document.querySelector('hotspots-image.has-active-hotspot')) {
          document.documentElement.classList.remove('hotspot-sheet-open');
        }
      });
    }

    /**
     * Keeps desktop cards within the viewport.
     * Mobile cards use a fixed bottom-sheet position and are not repositioned.
     */
    checkCardPosition() {
      if (!this.activeCard) return;

      this.activeCard.style.removeProperty('top');
      this.activeCard.style.removeProperty('right');
      this.activeCard.style.removeProperty('bottom');
      this.activeCard.style.removeProperty('left');

      if (!this.isDesktop()) return;

      const cardRect = this.activeCard.getBoundingClientRect();

      if (cardRect.bottom > document.documentElement.clientHeight) {
        this.activeCard.style.top = 'auto';
        this.activeCard.style.bottom = 'calc(100% + 12px)';
      }

      if (cardRect.right > window.innerWidth - 20) {
        this.activeCard.style.left =
          `calc(50% - ${cardRect.right - window.innerWidth + 45}px)`;
      } else if (cardRect.left < 20) {
        this.activeCard.style.left =
          `calc(50% - ${cardRect.left - 35}px)`;
      }
    }

    /**
     * Sets a hotspot's expanded and visible state.
     * @param {Element} hotspot
     * @param {boolean} active
     */
    setHotspotActiveState(hotspot, active) {
      hotspot.classList.toggle('is-active', active);

      const button = hotspot.querySelector('.hotspot__btn');
      const card = hotspot.querySelector('.hotspot__card');

      if (button) button.setAttribute('aria-expanded', active);
      if (card) card.setAttribute('aria-hidden', !active);

      this.activeHotspot = active ? hotspot : null;
    }
  }

  customElements.define('hotspots-image', HotspotsImage);
}
